import { z } from 'zod';
// ============================================================
// 프로모션 — 시크릿 코드(CODE) · 이벤트 쿠폰(EVENT) · 자동 보너스(AUTO)
// 정의서: Confluence 140514226 v0.7. kind 형태·혜택 필수·범위 규칙은 DB CHECK(0085)와 동일하게 두고,
// 기간 순서(endsAt > startsAt)·코드 접두어(FIKT)·문자열 형식은 여기(Zod)에서만 검증한다.
// ============================================================
// ── 상수 ──
export const PROMOTION_MAX_BONUS = 500;
export const PROMOTION_PERCENT_MAX = 90;
export const PROMOTION_FIXED_MIN = 100;
/** 할인 후 최소 결제 금액(원) */
export const PROMOTION_MIN_FINAL_AMOUNT = 100;
/** 시크릿 코드 — 영문 대문자·숫자 4~20자 */
export const PROMOTION_CODE_REGEX = /^[A-Z0-9]{4,20}$/;
/** 활성화 코드(FIKT-)와 충돌 방지 — 시크릿 코드로 등록 불가 */
export const PROMOTION_CODE_RESERVED_PREFIX = 'FIKT';
/** 이벤트 slug — 소문자·숫자·하이픈 3~40자 */
export const PROMOTION_SLUG_REGEX = /^[a-z0-9-]{3,40}$/;
/**
 * 보너스 크레딧 1개의 원가(원) — 프로모션 비용 계산용.
 * Gemini 이미지 생성 단가 약 $0.04/장 × 환율 1,400원 ≈ 56원을 보수적으로 60원으로 잡는다.
 * 모델·환율이 바뀌면 여기 한 곳만 조정한다.
 */
export const PROMOTION_BONUS_CREDIT_COST_KRW = 60;
// ── enum ──
export const PromotionKindSchema = z.enum(['AUTO', 'CODE', 'EVENT']);
export const PromotionDiscountTypeSchema = z.enum(['NONE', 'PERCENT', 'FIXED']);
/**
 * 절사 단위(FIKET-454) — 정률 할인 후 최종가를 이 단위 아래로 내림한다(1 = 절사 없음).
 * 표시 퍼센트는 입력값 그대로이고(50% 는 50%), 할인액·최종가만 절사 결과를 따른다. DB CHECK(0096)와 1:1.
 */
export const PROMOTION_TRUNCATE_UNITS = [1, 100, 1000];
export const PromotionTruncateUnitSchema = z.union([z.literal(1), z.literal(100), z.literal(1000)]);
export const PROMOTION_TRUNCATE_UNIT_LABELS = { 1: '없음', 100: '백원 절사', 1000: '천원 절사' };
export const PromotionStatusSchema = z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']);
export const CouponStatusSchema = z.enum(['ISSUED', 'RESERVED', 'USED', 'EXPIRED', 'REVOKED']);
// ── 이벤트 페이지 본문 (EVENT 전용) ──
export const PromotionPageContentSchema = z.object({
    title: z.string().min(1).max(60),
    paragraphs: z.array(z.string().max(500)).max(10),
    imageUrl: z.string().url().max(500).optional(),
    ctaLabel: z.string().min(1).max(20).default('쿠폰 받기'),
});
export function computeDiscount(listAmount, promo) {
    let discount = 0;
    if (promo.discountType === 'PERCENT') {
        discount = Math.floor((listAmount * promo.discountValue) / 100);
        const unit = promo.truncateUnit ?? 1;
        if (unit > 1)
            discount = listAmount - Math.floor((listAmount - discount) / unit) * unit;
    }
    else if (promo.discountType === 'FIXED')
        discount = Math.min(promo.discountValue, listAmount - PROMOTION_MIN_FINAL_AMOUNT);
    if (discount < 0)
        discount = 0;
    const finalAmount = listAmount - discount;
    const fixedTooBig = promo.discountType === 'FIXED' && promo.discountValue >= listAmount;
    const applicable = promo.discountType === 'NONE'
        || (finalAmount >= PROMOTION_MIN_FINAL_AMOUNT && !fixedTooBig);
    return {
        applicable,
        listAmount,
        discountAmount: applicable ? discount : 0,
        finalAmount: applicable ? finalAmount : listAmount,
    };
}
/** 프로모션 경제성 — 서버 집계값을 받아 비용·ROAS 를 한 정의로 계산한다. api·admin 공용 */
export function computePromotionEconomics(input) {
    const bonusCostKrw = input.bonusTotal * PROMOTION_BONUS_CREDIT_COST_KRW;
    const cost = input.discountTotal + bonusCostKrw;
    return { ...input, bonusCostKrw, cost, roas: cost > 0 ? input.revenue / cost : null };
}
/** 혜택 요약 문구 — 예 "전 상품 20% 할인 + 10크레딧", "대상 상품 2개 3,000원 할인" */
export function benefitSummary(promo) {
    const target = promo.productCodes === null ? '전 상품' : `대상 상품 ${promo.productCodes.length}개`;
    const notes = [];
    if (promo.discountBase === 'LIST')
        notes.push('정가 기준');
    if (promo.discountType === 'PERCENT' && promo.truncateUnit && promo.truncateUnit > 1)
        notes.push(PROMOTION_TRUNCATE_UNIT_LABELS[promo.truncateUnit]);
    const base = notes.length ? `(${notes.join(', ')})` : '';
    const parts = [];
    if (promo.discountType === 'PERCENT')
        parts.push(`${promo.discountValue}% 할인${base}`);
    else if (promo.discountType === 'FIXED')
        parts.push(`${promo.discountValue.toLocaleString('ko-KR')}원 할인${base}`);
    if (promo.bonusCredits > 0)
        parts.push(`${promo.bonusCredits}크레딧`);
    return `${target} ${parts.join(' + ')}`;
}
// ── 혜택 행 (promotion_benefits) — 이벤트 1개 = 혜택 N개, 행마다 상품 묶음 + 할인/보너스 ──
export const PromotionPageModeSchema = z.enum(['TEMPLATE', 'STATIC']);
export const CouponIssuedViaSchema = z.enum(['CLAIM', 'SIGNUP', 'ADMIN', 'BULK']);
/**
 * 할인 기준 금액 — SALE: 카탈로그 판매가(price, 할인의 할인) / LIST: 정가(basePrice, 카탈로그 할인을 대신함).
 * LIST 로 계산한 최종가가 판매가 이상이면 그 상품에는 적용 불가(고객이 코드 없이 사는 것보다 불리해지지 않게).
 */
export const PromotionDiscountBaseSchema = z.enum(['SALE', 'LIST']);
export const PromotionBenefitInputSchema = z.object({
    /** 수정 시 기존 행 식별. 없으면 새 행 */
    id: z.string().min(1).max(128).optional(),
    /** 표시명. null 이면 상품명으로 자동 */
    label: z.string().trim().min(1).max(40).nullable(),
    /** 대상 상품 code(ProductDefinition.type). 1개 이상 — "전체" 의미의 null 은 없다 */
    productCodes: z.array(z.string().min(1).max(64)).min(1, '대상 상품을 1개 이상 선택하세요'),
    discountType: PromotionDiscountTypeSchema,
    discountValue: z.number().int().min(0),
    /** 할인 기준 — 기본 SALE(판매가 위에 한 번 더). 구 클라이언트가 안 보내도 기존 동작 유지 */
    discountBase: PromotionDiscountBaseSchema.default('SALE'),
    /** 절사 단위 — 기본 1(없음). 정률 할인에서만 100·1000 허용(refineBenefits) */
    truncateUnit: PromotionTruncateUnitSchema.default(1),
    bonusCredits: z.number().int().min(0).max(PROMOTION_MAX_BONUS),
});
export const PromotionBenefitSchema = PromotionBenefitInputSchema.extend({ id: z.string(), sortOrder: z.number().int() });
/** 행 단위 규칙(DB CHECK 와 1:1) + 프로모션 단위 규칙(행 ≥1, 상품 중복 금지, AUTO 는 행 1개·NONE) */
export function refineBenefits(benefits, ctx, kind) {
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path, message });
    if (benefits.length === 0)
        issue(['benefits'], '혜택을 1개 이상 정하세요');
    benefits.forEach((b, i) => {
        if (b.discountType === 'NONE' && b.bonusCredits < 1)
            issue(['benefits', i, 'bonusCredits'], '할인이 없으면 보너스 크레딧은 1 이상이어야 합니다');
        if (b.discountType === 'PERCENT' && (b.discountValue < 1 || b.discountValue > PROMOTION_PERCENT_MAX))
            issue(['benefits', i, 'discountValue'], `정률 할인은 1~${PROMOTION_PERCENT_MAX}%`);
        if (b.discountType === 'FIXED' && b.discountValue < PROMOTION_FIXED_MIN)
            issue(['benefits', i, 'discountValue'], `정액 할인은 ${PROMOTION_FIXED_MIN}원 이상`);
        if (b.discountType === 'NONE' && b.discountValue !== 0)
            issue(['benefits', i, 'discountValue'], '할인 없음이면 할인 값은 0');
        if (b.discountType !== 'PERCENT' && b.truncateUnit !== 1)
            issue(['benefits', i, 'truncateUnit'], '절사는 정률 할인에서만 쓸 수 있습니다');
        if (kind === 'AUTO' && b.discountType !== 'NONE')
            issue(['benefits', i, 'discountType'], 'AUTO는 할인 없음 고정');
    });
    const seen = new Map();
    benefits.forEach((b, i) => {
        for (const code of b.productCodes) {
            const prev = seen.get(code);
            if (prev !== undefined && prev !== i)
                issue(['benefits'], `상품 ${code} 이(가) 두 혜택에 들어 있습니다. 상품은 한 혜택에만 넣을 수 있습니다`);
            seen.set(code, i);
        }
    });
    if (kind === 'AUTO' && benefits.length > 1)
        issue(['benefits'], 'AUTO는 혜택 1개만 가질 수 있습니다');
}
/** 목록·상세·쿠폰함 요약 — 행 1개면 행 문구, N개면 "상품별 N종" */
export function promotionBenefitsSummary(benefits) {
    if (benefits.length === 0)
        return '혜택 없음';
    if (benefits.length === 1)
        return benefitSummary(benefits[0]);
    return `상품별 ${benefits.length}종`;
}
// ── 공통 본문 ──
/**
 * 기본값 없는 원본 필드. 생성 스키마는 여기에 기본값을 얹고(PromotionBodySchema),
 * 수정 스키마는 이걸 partial 로 쓴다 — zod v4 는 partial 뒤에도 default 가 발동해 "누락 키 = 기본값" 이 되므로,
 * 기본값을 Fields 에 두지 않아야 PATCH 병합에서 기존 값이 덮이지 않는다.
 */
const PromotionFieldsSchema = z.object({
    kind: PromotionKindSchema,
    name: z.string().trim().min(1).max(60),
    description: z.string().trim().max(200).nullable().optional(),
    benefits: z.array(PromotionBenefitInputSchema).max(20),
    /** 이벤트 페이지 버튼으로 받기 허용(EVENT) */
    claimEnabled: z.boolean(),
    /** 진행 중이면 신규 가입 시 자동 발급(EVENT) */
    signupAutoIssue: z.boolean(),
    /** 대상 제한 자리 — 현재 서버는 null 만 허용 */
    eligibility: z.null(),
    pageMode: PromotionPageModeSchema,
    /** STATIC 일 때 정적 페이지 경로(/ 로 시작) */
    staticPath: z.string().trim().min(1).max(120).regex(/^\//, '/ 로 시작해야 합니다').nullable(),
    startsAt: z.string().datetime(),
    /** null = 무기한(CODE/EVENT만 허용) */
    endsAt: z.string().datetime().nullable(),
    /** CODE 총 사용 / EVENT 총 발급. null = 무제한. AUTO는 null 고정 */
    maxUses: z.number().int().positive().nullable(),
    /** EVENT 전용 — 발급일부터 N일 */
    couponValidDays: z.number().int().min(1).max(365).nullable(),
    /** CODE 전용 — 대문자 정규화 */
    code: z
        .string()
        .trim()
        .transform((s) => s.toUpperCase())
        .pipe(z.string().regex(PROMOTION_CODE_REGEX, '영문 대문자·숫자 4~20자'))
        .nullable(),
    /** EVENT 전용 — 생성 후 불변 */
    slug: z.string().trim().regex(PROMOTION_SLUG_REGEX, '소문자·숫자·하이픈 3~40자').nullable(),
    pageContent: PromotionPageContentSchema.nullable(),
    status: z.enum(['ACTIVE', 'PAUSED']),
});
const fields = PromotionFieldsSchema.shape;
/** 생성 본문 — 선택 필드 기본값 포함 */
const PromotionBodySchema = PromotionFieldsSchema.extend({
    claimEnabled: fields.claimEnabled.default(true),
    signupAutoIssue: fields.signupAutoIssue.default(false),
    eligibility: fields.eligibility.default(null),
    pageMode: fields.pageMode.default('TEMPLATE'),
    staticPath: fields.staticPath.default(null),
    maxUses: fields.maxUses.default(null),
    couponValidDays: fields.couponValidDays.default(null),
    code: fields.code.default(null),
    slug: fields.slug.default(null),
    pageContent: fields.pageContent.default(null),
    status: fields.status.default('ACTIVE'),
});
/**
 * kind별 형태 검증 — DB CHECK 제약과 1:1. 생성·수정(병합 후) 양쪽에서 같은 규칙을 쓴다.
 * 서버는 PATCH 본문을 기존 행과 병합한 뒤 이 함수로 재검증한다.
 */
export function refinePromotionShape(v, ctx) {
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path: [path], message });
    refineBenefits(v.benefits, ctx, v.kind);
    if (v.endsAt && Date.parse(v.endsAt) <= Date.parse(v.startsAt))
        issue('endsAt', '종료는 시작보다 뒤여야 합니다');
    if (v.kind !== 'EVENT' && v.staticPath !== null)
        issue('staticPath', 'EVENT 만 정적 경로를 가질 수 있습니다');
    switch (v.kind) {
        case 'AUTO':
            if (v.code !== null)
                issue('code', 'AUTO는 코드를 가질 수 없습니다');
            if (v.slug !== null)
                issue('slug', 'AUTO는 slug를 가질 수 없습니다');
            if (v.endsAt === null)
                issue('endsAt', 'AUTO는 종료일이 필수입니다');
            if (v.maxUses !== null)
                issue('maxUses', 'AUTO는 사용 한도가 없습니다');
            if (v.couponValidDays !== null)
                issue('couponValidDays', 'AUTO는 쿠폰 기한이 없습니다');
            break;
        case 'CODE':
            if (v.code === null)
                issue('code', '시크릿 코드는 필수입니다');
            else if (v.code.startsWith(PROMOTION_CODE_RESERVED_PREFIX))
                issue('code', 'FIKT로 시작하는 코드는 활성화 코드 전용입니다');
            if (v.slug !== null)
                issue('slug', 'CODE는 slug를 가질 수 없습니다');
            if (v.couponValidDays !== null)
                issue('couponValidDays', 'CODE는 쿠폰 기한이 없습니다');
            break;
        case 'EVENT':
            if (v.slug === null)
                issue('slug', '이벤트 slug는 필수입니다');
            if (v.code !== null)
                issue('code', 'EVENT는 코드를 가질 수 없습니다');
            if (v.couponValidDays === null)
                issue('couponValidDays', '쿠폰 사용 기한(일)은 필수입니다');
            if (v.pageMode === 'STATIC') {
                if (v.staticPath === null)
                    issue('staticPath', 'STATIC 페이지는 경로가 필수입니다');
            }
            else if (v.pageContent === null)
                issue('pageContent', '이벤트 페이지 내용은 필수입니다');
            break;
    }
}
export const PromotionCreateSchema = PromotionBodySchema.superRefine(refinePromotionShape);
/**
 * 수정 — kind·slug·status 불변(status 는 /status 전이 API). 기본값 없는 Fields 의 partial 이라
 * 누락 키는 undefined(변경 없음). 서버가 기존 행과 병합 후 refinePromotionShape 재검증.
 */
export const PromotionUpdateSchema = PromotionFieldsSchema.omit({ kind: true, slug: true, status: true }).partial();
/** 어드민 응답 — 저장된 프로모션 1건 */
export const PromotionSchema = PromotionBodySchema.extend({
    id: z.string(),
    status: PromotionStatusSchema,
    createdBy: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    benefits: z.array(PromotionBenefitSchema),
    /** @deprecated 0090 에서 제거 — benefits[0] 미러 */
    discountType: PromotionDiscountTypeSchema,
    /** @deprecated 0090 에서 제거 — benefits[0] 미러 */
    discountValue: z.number().int(),
    /** @deprecated 0090 에서 제거 — benefits[0] 미러 */
    bonusCredits: z.number().int(),
    /** @deprecated 0090 에서 제거 — benefits[0] 미러 */
    productCodes: z.array(z.string()).nullable(),
});
/** 어드민 목록 행 — 집계 포함 */
export const PromotionListRowSchema = PromotionSchema.extend({
    /** EVENT: 발급 수(REVOKED 제외). 그 외 0 */
    issuedCount: z.number().int(),
    /** EVENT: 발급 대상 유저 수 */
    issuedUsers: z.number().int().optional(),
    /** CODE/EVENT: 적용 결제 수(FAILED·환불 해제 제외). AUTO: 지급 lot 수 */
    usedCount: z.number().int(),
    /** CODE/EVENT: 할인 총액(원) */
    discountTotal: z.number().int(),
    /** 지급 보너스 크레딧 합 */
    bonusTotal: z.number().int(),
    /** CODE/EVENT: 사용 결제 순매출(결제액 − 환불액). AUTO 는 0 */
    revenue: z.number().int(),
    /** 매출 / (할인 + 보너스 원가) 비율. 비용 0 또는 AUTO 는 null — computePromotionEconomics 와 같은 정의 */
    roas: z.number().nullable(),
    /** EVENT: 사용 쿠폰/발급 쿠폰, CODE: 코드 확인 후 결제한 사용자/확인 사용자. 분모 0·AUTO 는 null */
    conversionRate: z.number().nullable(),
});
/** 상품 1개에 대한 혜택 계산 결과 — 미리보기·어드민 폼·이벤트 페이지 공용 */
export const PromotionPreviewRowSchema = z.object({
    applicable: z.boolean(),
    listAmount: z.number().int(),
    discountAmount: z.number().int(),
    finalAmount: z.number().int(),
    bonusCredits: z.number().int(),
    benefitId: z.string().nullable().optional(),
    /** 적용된 혜택 행의 할인 방식·값·기준 — 웹 카드 배지("코드 15% 할인", 정가 기준 표기) 표시용. 대상 아님이면 생략 */
    discountType: PromotionDiscountTypeSchema.optional(),
    discountValue: z.number().int().optional(),
    discountBase: PromotionDiscountBaseSchema.optional(),
});
export const PromotionPreviewResponseSchema = z.object({
    name: z.string(),
    benefitSummary: z.string(),
    /** 키 = 상품 code */
    products: z.record(z.string(), PromotionPreviewRowSchema),
});
/** 결제에 적용된 프로모션 요약(confirm 응답·결제 내역·어드민 표시) */
export const PromotionAppliedSchema = z.object({
    kind: PromotionKindSchema,
    code: z.string().nullable(),
    name: z.string(),
    listAmount: z.number().int(),
    discountAmount: z.number().int(),
    bonusCredits: z.number().int(),
    releasedAt: z.string().nullable().optional(),
    benefitLabel: z.string().nullable().optional(),
});
/** 쿠폰함·어드민 발급 내역 */
export const CouponSchema = z.object({
    id: z.string(),
    promotionId: z.string(),
    promotionName: z.string(),
    benefitSummary: z.string(),
    productCodes: z.array(z.string()).nullable(),
    status: CouponStatusSchema,
    issuedAt: z.string(),
    expiresAt: z.string(),
    usedAt: z.string().nullable(),
    paymentId: z.string().nullable(),
    benefitId: z.string().optional(),
    benefitLabel: z.string().nullable().optional(),
    issuedVia: CouponIssuedViaSchema.optional(),
    issueReason: z.string().nullable().optional(),
    extendedAt: z.string().nullable().optional(),
});
/** 이벤트 페이지 공개 정보 */
export const PromotionPublicSchema = z.object({
    slug: z.string(),
    name: z.string(),
    benefitSummary: z.string(),
    pageContent: PromotionPageContentSchema,
    startsAt: z.string(),
    endsAt: z.string().nullable(),
    couponValidDays: z.number().int(),
    /** ACTIVE 기준 파생: upcoming / open / closed */
    phase: z.enum(['upcoming', 'open', 'closed']),
    myCoupon: z.object({ id: z.string(), status: CouponStatusSchema }).nullable(),
});
// ── 고객 API 계약 (릴리스 B) ──
/** 미리보기 요청 — 코드 또는 쿠폰 중 하나. 예약을 만들지 않는다 */
/**
 * 코드가 어디서 왔는지 — 'link' 는 프로모션 페이지 링크(/purchase?promo=)로 넘어와 자동 확인된 것,
 * 'manual' 은 사용자가 직접 입력한 것. 감사 로그에 남겨 두 경로의 퍼널을 따로 본다 (FIKET-445)
 */
export const PromotionPreviewSourceSchema = z.enum(['link', 'manual']);
export const PromotionPreviewRequestSchema = z.union([
    z
        .object({
        code: z
            .string()
            .trim()
            .transform((s) => s.toUpperCase())
            .pipe(z.string().regex(PROMOTION_CODE_REGEX, '코드는 영문·숫자 4~20자입니다')),
        source: PromotionPreviewSourceSchema.optional(),
    })
        .strict(),
    z.object({ couponId: z.string().min(1).max(128) }).strict(),
]);
/** 고객에게 돌아가는 판정 실패 코드(ERROR_CODES 부분집합) */
export const PromotionErrorCodeSchema = z.enum([
    'PROMOTION_INVALID',
    'PROMOTION_NOT_STARTED',
    'PROMOTION_EXPIRED',
    'PROMOTION_EXHAUSTED',
    'PROMOTION_ALREADY_USED',
    'PROMOTION_NOT_APPLICABLE',
    'COUPON_UNAVAILABLE',
    'COUPON_EXPIRED',
    'COUPON_RESERVED',
    'PROMOTION_CLAIM_DISABLED',
]);
/** 고객 문구(정의서 F-02). web promotionError.ts 와 api 응답 message 가 같은 소스를 쓴다 */
export const PROMOTION_ERROR_MESSAGES = {
    PROMOTION_INVALID: '유효하지 않은 코드입니다',
    PROMOTION_NOT_STARTED: '아직 사용할 수 없는 코드입니다',
    PROMOTION_EXPIRED: '사용 기간이 끝난 코드입니다',
    PROMOTION_EXHAUSTED: '사용 가능 수량이 모두 소진되었습니다',
    PROMOTION_ALREADY_USED: '이미 사용한 코드입니다',
    PROMOTION_NOT_APPLICABLE: '이 상품에는 프로모션이 적용되지 않습니다',
    COUPON_UNAVAILABLE: '사용할 수 없는 쿠폰입니다',
    COUPON_EXPIRED: '유효기간이 지난 쿠폰입니다',
    COUPON_RESERVED: '이 쿠폰으로 진행 중인 결제가 있습니다',
    PROMOTION_CLAIM_DISABLED: '이 이벤트는 쿠폰을 직접 받을 수 없습니다',
};
//# sourceMappingURL=promotion.js.map